import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { showNotification } from '@client/redux/notification/notificationActions';
import {
  PORTALS,
  SIGN_IN_FAILURE,
  cancelMfaChallenge,
  LOGIN_NOTICE,
  sendPasswordReset,
  takeLoginNotice,
} from '@client/auth';
import {
  signInAdvertiser,
  submitAdvertiserMfaCode,
} from '@client/redux/loggedInUser/loggedInUserActions';
import { MFA_CHALLENGE_REQUIRED } from '@client/redux/loggedInUser/loggedInUserReducer';
import FormWrapper from '@client/components/inputs/formWrapper/FormWrapper';
import ButtonWithLoader from '@client/components/ButtonWithLoader/ButtonWithLoader';
import OtpInput from '@client/components/inputs/otpInput/OtpInput';
import { OTP_LENGTH } from '@client/shared/utils/otpUtils/otpUtils';
import {
  formLoginValidationSchema,
  formResetPasswordValidationSchema,
} from '@client/shared/utils/validationSchemas/validationSchemas';
import InputErrorMessage from '@client/components/inputErrorMessage/InputErrorMessage';
import styleHelper from '@client/shared/styles/styleHelper.scss';
import Flex from '@client/components/flex/Flex';
import Input from '@client/components/inputs/input/Input';
import Image from '@client/components/images/image/Image';
import sparkLogo from '@client/shared/icons/ic-spark.svg';
import iconLeftArrow from '@client/shared/icons/chevron-left.svg';
import { NOTIFICATION_TYPE } from '@client/containers/notificationListing/notification/Notification';
import style from './loginBox.scss';

const RESET_EMAIL_FAILED_MESSAGE =
  "We couldn't send the email. Please try again shortly.";

const ENROLLED_NOTICE =
  "Your authenticator is set up. Sign in again and enter a new code from your app (the one you just used can't be used twice).";

const LOGIN_NOTICES = {
  [LOGIN_NOTICE.ENROLLED]: ENROLLED_NOTICE,
  [LOGIN_NOTICE.REMOVED]:
    'Your authenticator app was removed. You can sign in with your password, and set up a new one from the settings menu.',
};

const mfaInitialData = {
  code: '',
};

const loginInitialData = {
  password: '',
  email: '',
};

const resetPasswordInitialData = {
  email: '',
};

const AdvertiserLoginPage = () => {
  const [isLoginform, setIsLoginForm] = useState(true);
  const [isChallenging, setIsChallenging] = useState(false);
  // Set when the user was sent back here after setting up an authenticator.
  const [notice, setNotice] = useState(
    () => LOGIN_NOTICES[takeLoginNotice(PORTALS.ADVERTISER)] || null
  );
  const dispatch = useDispatch();
  const history = useHistory();

  const handleLoginSubmit = (values, { setErrors, setSubmitting }) => {
    const { email, password } = values;

    setNotice(null);
    dispatch(signInAdvertiser(email, password)).then((res) => {
      setSubmitting(false);
      if (res && res.payload) {
        history.push('/advertiser/dashboard');
      } else if (res && res.type === MFA_CHALLENGE_REQUIRED) {
        setIsChallenging(true);
      } else {
        setErrors({ password: res.error });
      }
    });
  };

  // A challenge the user walks away from (or leaves by unmounting) is dropped.
  useEffect(() => cancelMfaChallenge, []);

  const backToSignIn = () => {
    cancelMfaChallenge();
    setIsChallenging(false);
  };

  const handleMfaSubmit = (values, { setErrors, setSubmitting }) => {
    dispatch(submitAdvertiserMfaCode(values.code)).then((res) => {
      setSubmitting(false);
      if (res && res.payload) {
        history.push('/advertiser/dashboard');
      } else if (res.reason === SIGN_IN_FAILURE.CHALLENGE_EXPIRED) {
        backToSignIn();
      } else {
        setErrors({ code: res.error });
      }
    });
  };

  const handleForgotPassword = (values, { setSubmitting }) => {
    const { email } = values;

    sendPasswordReset(PORTALS.ADVERTISER, email)
      .then(() => {
        setSubmitting(false);
        dispatch(
          showNotification(
            'Please check your inbox!',
            NOTIFICATION_TYPE.SUCCESS
          )
        );
      })
      .catch(() => {
        setSubmitting(false);
        dispatch(
          showNotification(
            RESET_EMAIL_FAILED_MESSAGE,
            NOTIFICATION_TYPE.WARNING
          )
        );
      });
  };

  const renderLoginForm = (formProps) => {
    const onFormChange = (fieldName, value) => {
      formProps.setFieldValue(fieldName, value);
    };

    const formValues = formProps.values;
    const { errors, touched } = formProps;

    return (
      <form onSubmit={formProps.handleSubmit}>
        <Flex>
          <div
            className={style.backNavWrapper}
            onClick={() => history.push('/')}
          >
            <Image height="24" width="24" src={iconLeftArrow} />
            <span
              className={classNames(
                styleHelper.mgl16,
                styleHelper.text8,
                style.backNav
              )}
            >
              Back to allersparks.com
            </span>
          </div>
          <div className={style.advertiserLoginPage}>
            <div className={style.panel}>
              <Flex column alignCenter className={style.inputFieldWrapper}>
                <Image height="168" width="168" src={sparkLogo} />
                {notice && (
                  <span
                    className={classNames(
                      styleHelper.mgt24,
                      styleHelper.text8,
                      style.notice
                    )}
                  >
                    {notice}
                  </span>
                )}
                <Input
                  autoComplete="nope"
                  className={classNames(
                    styleHelper.inputLg,
                    styleHelper.mgt24,
                    style.input
                  )}
                  value={formValues.email}
                  onChange={(e) => onFormChange('email', e.target.value)}
                  placeholder="E-mail"
                  id="email"
                  name="email"
                  type="text"
                />
                <InputErrorMessage
                  errorMessage={touched.email && errors.email}
                  className={style.error}
                />
                <Input
                  autoComplete="nope"
                  className={classNames(styleHelper.inputLg, style.input)}
                  value={formValues.password}
                  onChange={(e) => onFormChange('password', e.target.value)}
                  placeholder="Password"
                  id="password"
                  name="Password"
                  type="password"
                />
                <InputErrorMessage
                  errorMessage={touched.password && errors.password}
                  className={style.error}
                />
                <button
                  className={classNames(
                    styleHelper.btnContained,
                    style.resetButton
                  )}
                  onClick={formProps.handleSubmit}
                >
                  LOG IN
                </button>
                <span
                  className={classNames(
                    styleHelper.mgt24,
                    styleHelper.text8,
                    style.forgotPasswordText
                  )}
                  onClick={() => setIsLoginForm(false)}
                >
                  {' '}
                  Forgot password?
                </span>
              </Flex>
            </div>
          </div>
        </Flex>
      </form>
    );
  };

  const renderCodeForm = (formProps, { heading, intro, footer }) => {
    const { errors, touched } = formProps;

    return (
      <form onSubmit={formProps.handleSubmit}>
        <div className={style.advertiserPasswordResetPage}>
          <div className={style.panel}>
            <Flex column alignCenter className={style.inputFieldWrapper}>
              <Image height="88" width="88" src={sparkLogo} />
              <span
                className={classNames(
                  styleHelper.mgt24,
                  styleHelper.text1,
                  styleHelper.textCenter
                )}
              >
                {heading}
              </span>
              <span
                className={classNames(
                  styleHelper.mgt24,
                  styleHelper.text8,
                  style.mfaText
                )}
              >
                {intro}
              </span>
              <OtpInput
                className={styleHelper.mgt24}
                value={formProps.values.code}
                onChange={(code) => formProps.setFieldValue('code', code)}
                onEnter={
                  formProps.isSubmitting ? undefined : formProps.handleSubmit
                }
                hasError={!!errors.code}
                autoFocus
              />
              <InputErrorMessage
                errorMessage={touched.code && errors.code}
                className={style.error}
              />
              {errors.code && !touched.code && (
                <span className={style.mfaError}>{errors.code}</span>
              )}
              <ButtonWithLoader
                label="VERIFY"
                className={classNames(
                  styleHelper.btnContained,
                  style.resetButton
                )}
                onClick={formProps.handleSubmit}
                loading={formProps.isSubmitting}
                disabled={(formProps.values.code || '').length < OTP_LENGTH}
              />
              {footer.action}
            </Flex>
          </div>
        </div>
      </form>
    );
  };

  const renderMfaForm = (formProps) =>
    renderCodeForm(formProps, {
      heading: 'AUTHENTICATION CODE',
      intro:
        'Open your authenticator app and enter the current code. Lost your device? Contact support.',
      footer: {
        action: (
          <span
            className={classNames(
              styleHelper.mgt24,
              styleHelper.text8,
              style.mfaText
            )}
            onClick={backToSignIn}
          >
            Back to login
          </span>
        ),
      },
    });

  const renderResetForm = (formProps) => {
    const onFormChange = (fieldName, value) => {
      formProps.setFieldValue(fieldName, value);
    };

    const formValues = formProps.values;
    const { errors, touched } = formProps;
    return (
      <form onSubmit={formProps.handleSubmit}>
        <div className={style.advertiserPasswordResetPage}>
          <div className={style.panel}>
            <div
              className={style.loginNavWrapper}
              onClick={() => setIsLoginForm(true)}
            >
              <Image height="24" width="24" src={iconLeftArrow} />
              <span
                className={classNames(
                  styleHelper.mgl16,
                  styleHelper.text8,
                  style.loginNav
                )}
              >
                Back to login
              </span>
            </div>
            <Flex column alignCenter className={style.inputFieldWrapper}>
              <Image height="88" width="88" src={sparkLogo} />
              <span
                className={classNames(styleHelper.mgt24, styleHelper.text1)}
              >
                RESET PASSWORD
              </span>
              <Input
                autoComplete="off"
                className={classNames(
                  styleHelper.inputLg,
                  styleHelper.mgt48,
                  style.input
                )}
                value={formValues.email}
                onChange={(e) => onFormChange('email', e.target.value)}
                placeholder="Repeat e-mail"
                id="email"
                name="email"
                type="text"
              />
              <InputErrorMessage
                errorMessage={touched.email && errors.email}
                className={style.error}
              />
              <button
                className={classNames(
                  styleHelper.btnContained,
                  style.resetButton
                )}
                onClick={formProps.handleSubmit}
                disabled={formProps.isSubmitting}
              >
                SEND RESET LINK
              </button>
            </Flex>
          </div>
        </div>
      </form>
    );
  };

  if (isChallenging) {
    return (
      <FormWrapper
        key="challenge"
        initialValues={mfaInitialData}
        onSubmit={handleMfaSubmit}
        component={renderMfaForm}
      />
    );
  }

  return (
    <FormWrapper
      initialValues={isLoginform ? loginInitialData : resetPasswordInitialData}
      onSubmit={isLoginform ? handleLoginSubmit : handleForgotPassword}
      component={isLoginform ? renderLoginForm : renderResetForm}
      validationSchema={
        isLoginform
          ? formLoginValidationSchema
          : formResetPasswordValidationSchema
      }
    />
  );
};

export default connect()(withRouter(AdvertiserLoginPage));
